/* eslint-disable @typescript-eslint/no-unused-vars */
import { Router, NavigationEnd, RouterLinkActive, RouterLink } from '@angular/router';
import { DOCUMENT, NgClass } from '@angular/common';
import {
  Component,
  Inject,
  ElementRef,
  OnInit,
  Renderer2,
  HostListener,
  OnDestroy,
} from '@angular/core';
import { ROUTES } from './sidebar-items';
import { AuthService, Role } from '@core';
import { RouteInfo } from './sidebar.metadata';
import { TranslateModule } from '@ngx-translate/core';
import { NgScrollbar } from 'ngx-scrollbar';
import { UnsubscribeOnDestroyAdapter } from '@shared';
@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  standalone: true,
  imports: [
    NgScrollbar,
    RouterLinkActive,
    RouterLink,
    NgClass,
    TranslateModule,
  ],
})
export class SidebarComponent extends UnsubscribeOnDestroyAdapter implements OnInit {
  public sidebarItems!: RouteInfo[];
  public innerHeight?: number;
  public bodyTag!: HTMLElement;
  listMaxHeight?: string;
  listMaxWidth?: string;
  userFullName?: string;
  userImg?: string;
  userType?: string;
  schoolName?: string;
  headerHeight = 60;
  currentRoute?: string;
  constructor(
    @Inject(DOCUMENT) private document: Document,
    private renderer: Renderer2,
    public elementRef: ElementRef,
    private authService: AuthService,
    private router: Router
  ) {
    super();
    this.elementRef.nativeElement.closest('body');
    this.subs.sink = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        // close sidebar on mobile screen after menu select
        this.renderer.removeClass(this.document.body, 'overlay-open');
      }
    });
  }
  @HostListener('window:resize', ['$event'])
  windowResizecall() {
    this.setMenuHeight();
    this.checkStatuForResize(false);
  }
  @HostListener('document:mousedown', ['$event'])
  onGlobalClick(event: Event): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.renderer.removeClass(this.document.body, 'overlay-open');
    }
  }
  callToggleMenu(event: Event, length: number) {
    if (length > 0) {
      const parentElement = (event.target as HTMLInputElement).closest('li');
      const activeClass = parentElement?.classList.contains('active');

      if (activeClass) {
        this.renderer.removeClass(parentElement, 'active');
      } else {
        this.renderer.addClass(parentElement, 'active');
      }
    }
  }
  ngOnInit() {
    if (this.authService.isLoggedIn()) {
      const currentUser = this.authService.currentUserValue!;
      const userRole = currentUser.role;
      this.userFullName =
        currentUser.firstName +
        ' ' +
        currentUser.lastName;
      
      // Set user image based on role
      switch (userRole) {
        case Role.SchoolDirector:
          this.userImg = 'assets/images/user/school_director.png';
          break;
        case Role.Teacher:
          this.userImg = 'assets/images/user/default-teacher.webp';
          break;
        case Role.Admin:
          this.userImg = 'assets/images/user/admin.jpg';
          break;
        case Role.Student:
          this.userImg = 'assets/images/user/user1.jpg';
          break;
        case Role.Parent:
          this.userImg = 'assets/images/user/default_parent.jpeg';
          break;
        case Role.Doctor:
          this.userImg = 'assets/images/user/doctor_default.jpeg';
          break;
        default:
          this.userImg = 'assets/images/user/school_director.png';
      }

      const roleAliases: Record<string, string[]> = {
        [Role.Admin]: ['Admin'],
        [Role.SchoolDirector]: ['SchoolDirector'],
        [Role.Teacher]: ['Teacher'],
        [Role.Parent]: ['Parent'],
        [Role.Doctor]: ['Doctor'],
        [Role.Student]: ['Student'],
        [Role.All]: ['All'],
      };

      const matchesRole = (routeRoles: string[], role: string) => {
        if (routeRoles.includes('All') || routeRoles.includes(Role.All)) return true;
        if (routeRoles.includes(role)) return true;
        const aliases = roleAliases[role] || [];
        return aliases.some((a) => routeRoles.includes(a));
      };

      const filterMenuItems = (items: RouteInfo[]): RouteInfo[] => {
        return items
          .filter((item) => matchesRole(item.role, userRole))
          .map((item) => ({
            ...item,
            submenu: item.submenu && item.submenu.length > 0
              ? filterMenuItems(item.submenu)
              : item.submenu,
          }));
      };

      this.sidebarItems = filterMenuItems(ROUTES);
      const roleLabels: Record<string, string> = {
        [Role.Admin]: 'Admin',
        [Role.SchoolDirector]: 'School Director',
        [Role.Teacher]: 'Teacher',
        [Role.Parent]: 'Parent',
        [Role.Doctor]: 'Doctor',
        [Role.Student]: 'Student',
        [Role.All]: 'User',
      };
      this.userType = roleLabels[userRole] ?? 'User';
      this.schoolName = currentUser.schoolName || '';
    }

    this.initLeftSidebar();
    this.bodyTag = this.document.body;
  }
  initLeftSidebar() {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const _this = this;
    // Set menu height
    _this.setMenuHeight();
    _this.checkStatuForResize(true);
  }
  setMenuHeight() {
    this.innerHeight = window.innerHeight;
    const height = this.innerHeight - this.headerHeight;
    this.listMaxHeight = height + '';
    this.listMaxWidth = '500px';
  }
  isOpen() {
    return this.bodyTag.classList.contains('overlay-open');
  }
  checkStatuForResize(firstTime: boolean) {
    if (window.innerWidth < 1025) {
      this.renderer.addClass(this.document.body, 'ls-closed');
    } else {
      this.renderer.removeClass(this.document.body, 'ls-closed');
    }
  }
  mouseHover() {
    const body = this.elementRef.nativeElement.closest('body');
    if (body.classList.contains('submenu-closed')) {
      this.renderer.addClass(this.document.body, 'side-closed-hover');
      this.renderer.removeClass(this.document.body, 'submenu-closed');
    }
  }
  mouseOut() {
    const body = this.elementRef.nativeElement.closest('body');
    if (body.classList.contains('side-closed-hover')) {
      this.renderer.removeClass(this.document.body, 'side-closed-hover');
      this.renderer.addClass(this.document.body, 'submenu-closed');
    }
  }
  logout() {
    this.subs.sink = this.authService.logout().subscribe((res) => {
      if (res.success) {
        this.router.navigate(['/authentication/signin']);
      }
    });
  }
}
